import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Stack, Text } from '@repletoryreach/sdk-ui';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { useBots, botName } from '../lib/bots';
import { AddBotModal } from '../pages/settings/assistant/components/AddBotModal';
import { useT } from '../i18n/I18nProvider';

/**
 * Which chatbot a page is looking at.
 *
 * By default it renders nothing for an organisation with one chatbot, so a
 * page that is the same for every chatbot stays quiet (21 September 2026).
 * `always` shows it with one chatbot too, and `withAdd` puts the way to add
 * another under it: the Flows sidebar asks for both, because Flows is where a
 * chatbot's work is done and the owner went there looking for them (22
 * September 2026).
 */
export function BotSwitcher({ description, always = false, withAdd = false }: {
  description?: string;
  always?: boolean;
  withAdd?: boolean;
}) {
  const t = useT();
  const { bots, multi, active, setActive, canAdd, allowance } = useBots();
  const [adding, setAdding] = useState(false);
  if (!active || (!multi && !always)) return null;
  const fallback = t('settings.assistant.defaultName');
  return (
    <Stack gap={6}>
      <Select
        label={t('bots.switcher.label')}
        description={description ?? t('bots.switcher.hint')}
        value={active.id}
        onChange={(e) => setActive(e.currentTarget.value)}
        data={bots.map((b) => ({
          value: b.id,
          label: b.is_default ? t('bots.switcher.defaultOption', { name: botName(b, fallback) }) : botName(b, fallback)
        }))}
      />
      {withAdd && (canAdd ? (
        <Button size="xs" variant="subtle" onClick={() => setAdding(true)} style={{ alignSelf: 'flex-start' }}>
          {t('bots.add.action')}
        </Button>
      ) : (
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('bots.add.atLimit', { plan: allowance?.plan_name ?? '', count: allowance?.allowance ?? bots.length })}{' '}
          <Link to="/billing">{t('bots.add.seePlans')}</Link>
        </Text>
      ))}
      {adding && <AddBotModal opened={adding} onClose={() => setAdding(false)} onAdded={setActive} />}
    </Stack>
  );
}
